import type { Header, Table as ReactTable, RowData } from "@tanstack/react-table";
import { EyeOn } from "components/icons/icn-eye-on.icons";
import type { ReactNode } from "react";
import intl from "react-intl-universal";
import {
  getColumnSizeStyle,
  getPinnedColumnStyle,
  getStickyScrollMarginStyle,
} from "../../table-pinning";
import { HeaderCell, HeaderCellContent, HeaderRow, HideColumnButton } from "./styled";

export interface TableHeaderProps<TData extends RowData> {
  readonly table: ReactTable<TData>;
  readonly renderHeader: (header: Header<TData, unknown>) => ReactNode;
}

const TableHeader = <TData extends RowData>(props: TableHeaderProps<TData>) => {
  const { table, renderHeader } = props;

  const leftGroups = table.getLeftHeaderGroups();
  const centerGroups = table.getCenterHeaderGroups();
  const rightGroups = table.getRightHeaderGroups();
  const scrollMarginStyle = getStickyScrollMarginStyle(table);

  return (
    <thead>
      {centerGroups.map((headerGroup, index) => {
        const headers = [
          ...(leftGroups[index]?.headers ?? []),
          ...headerGroup.headers,
          ...(rightGroups[index]?.headers ?? []),
        ];

        return (
          <HeaderRow key={headerGroup.id}>
            {headers.map((header) => (
              <HeaderCell
                key={header.id}
                scope="col"
                data-pinned={header.column.getIsPinned() || undefined}
                style={{
                  ...scrollMarginStyle,
                  ...getColumnSizeStyle(header.column),
                  ...getPinnedColumnStyle(header.column, table),
                }}
              >
                <HeaderCellContent>
                  {header.isPlaceholder ? null : renderHeader(header)}
                  {header.column.getCanHide() && (
                    <HideColumnButton
                      type="button"
                      aria-label={intl.get("commons.table.hideColumn", {
                        column:
                          typeof header.column.columnDef.header === "string"
                            ? header.column.columnDef.header
                            : header.column.id,
                      })}
                      onClick={() => {
                        if (header.column.getIsVisible()) {
                          header.column.toggleVisibility(false);
                        }
                      }}
                    >
                      <EyeOn />
                    </HideColumnButton>
                  )}
                </HeaderCellContent>
              </HeaderCell>
            ))}
          </HeaderRow>
        );
      })}
    </thead>
  );
};

export default TableHeader;
